import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase, isSupabaseConfigured } from '../services/supabaseClient';

const DISMISS_KEY = 'finncal_announcement_dismissed';

const AnnouncementBar = () => {
  const [coupon, setCoupon] = useState(null);
  const [customBanner, setCustomBanner] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(DISMISS_KEY)) {
      setDismissed(true);
      return;
    }
    if (!isSupabaseConfigured()) return;

    // Check admin-controlled site banner first
    supabase
      .from('app_settings')
      .select('value')
      .eq('key', 'announcement_banner')
      .single()
      .then(({ data }) => {
        if (data?.value?.enabled && data.value.text) {
          setCustomBanner(data.value.text);
          return;
        }
        // Fall back to discount coupon banner
        const now = new Date().toISOString();
        supabase
          .from('discount_coupons')
          .select('code, discount_type, discount_value, promo_label, valid_until')
          .eq('is_active', true)
          .eq('show_on_pricing', true)
          .or(`valid_until.is.null,valid_until.gt.${now}`)
          .order('created_at', { ascending: false })
          .limit(1)
          .single()
          .then(({ data: couponData }) => {
            if (couponData) setCoupon(couponData);
          });
      });
  }, []);

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(coupon.code).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatDiscount = (c) => {
    if (c.discount_type === 'free') return '100% free';
    if (c.discount_type === 'percentage') return `${c.discount_value}% off`;
    return `₹${c.discount_value} off`;
  };

  if (dismissed || (!customBanner && !coupon)) return null;

  return (
    <div style={styles.bar}>
      <div style={styles.inner}>
        {customBanner ? (
          <span style={styles.text}>{customBanner}</span>
        ) : (
          <>
            {coupon.promo_label && <span style={styles.tag}>{coupon.promo_label}</span>}
            <span style={styles.text}>
              Use code{' '}
              <button style={styles.codeBtn} onClick={handleCopy} title="Copy code">
                {coupon.code}
              </button>{' '}
              for {formatDiscount(coupon)} on paid plans.
            </span>
            {copied && <span style={styles.copied}>Copied!</span>}
            <Link to="/pricing" style={styles.link}>
              View Plans
            </Link>
          </>
        )}
      </div>
      <button style={styles.closeBtn} onClick={handleDismiss} aria-label="Dismiss">
        &times;
      </button>
    </div>
  );
};

const styles = {
  bar: {
    background: 'linear-gradient(90deg, #1e3a5f 0%, #2563eb 100%)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.55rem 1rem',
    fontSize: '0.85rem',
    position: 'relative',
    gap: '0.5rem',
  },
  inner: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  tag: {
    background: 'rgba(255,255,255,0.2)',
    borderRadius: '4px',
    padding: '1px 7px',
    fontSize: '0.75rem',
    fontWeight: '600',
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
  },
  text: {
    opacity: 0.95,
  },
  codeBtn: {
    background: 'rgba(255,255,255,0.18)',
    border: '1px dashed rgba(255,255,255,0.5)',
    color: '#fff',
    borderRadius: '4px',
    padding: '1px 8px',
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    fontWeight: '700',
    cursor: 'pointer',
    letterSpacing: '0.05em',
  },
  copied: {
    color: '#86efac',
    fontWeight: '600',
    fontSize: '0.8rem',
  },
  link: {
    color: '#fff',
    fontWeight: '600',
    textDecoration: 'underline',
    whiteSpace: 'nowrap',
    fontSize: '0.82rem',
    opacity: 0.9,
  },
  closeBtn: {
    position: 'absolute',
    right: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.7)',
    fontSize: '1.1rem',
    cursor: 'pointer',
    lineHeight: 1,
    padding: '0 4px',
  },
};

export default AnnouncementBar;
